"use client";

import { useEffect, useRef, useCallback, useState } from "react"; // Added useState
import L, { Map as LeafletMap, Marker, LatLngBounds } from "leaflet";
import "leaflet/dist/leaflet.css";
import { Issue } from "@/types";

// Fix default marker icon paths
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

/* =========================
   RISK COLOR HELPER
   ========================= */
const getRiskColor = (riskScore?: number) => {
  if (!riskScore) return "#9ca3af"; // gray
  if (riskScore >= 70) return "#ef4444"; // red
  if (riskScore >= 40) return "#f59e0b"; // yellow
  return "#10b981"; // green
};

/* =========================
   CUSTOM RISK-BASED ICON
   ========================= */
const createCustomIcon = (
  riskScore?: number,
  isSelected: boolean = false
) => {
  const size = isSelected ? 36 : 26;
  const color = getRiskColor(riskScore);
  const className = isSelected ? "selected-marker pulse" : "";

  return L.divIcon({
    html: `
      <div class="custom-marker ${className}" style="
        background-color: ${color};
        width: ${size}px;
        height: ${size}px;
        border: 3px solid white;
        border-radius: 50%;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-weight: bold;
        font-size: 11px;
      ">
        ${riskScore ? Math.min(99, riskScore) : "!"}
      </div>
    `,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    className: "custom-div-icon",
  });
};

interface IssueMapProps {
  issues: Issue[];
  selectedIssue?: Issue | null;
  onIssueSelect?: (issue: Issue) => void;
  className?: string;
  height?: number | string;
  zoomToBounds?: boolean;
}

export default function IssueMap({
  issues,
  selectedIssue,
  onIssueSelect,
  className = "",
  height = 500,
  zoomToBounds = false,
}: IssueMapProps) {
  const mapRef = useRef<LeafletMap | null>(null);
  const markersRef = useRef<Map<string, Marker>>(new Map());
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const [showLegend, setShowLegend] = useState(true); // Added legend state

  /* =========================
     FIT MAP TO MARKERS
     ========================= */
  const fitMapToMarkers = useCallback(() => {
    if (!mapRef.current || markersRef.current.size === 0) return;

    const bounds = new LatLngBounds([]);
    markersRef.current.forEach((marker) => {
      bounds.extend(marker.getLatLng());
    });

    if (bounds.isValid()) {
      mapRef.current.fitBounds(bounds, {
        padding: [20, 20],
        maxZoom: 15,
        animate: true,
      });
    }
  }, []);

  /* =========================
     INIT MAP
     ========================= */
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    const map = L.map(mapContainerRef.current).setView(
      [20.5937, 78.9629],
      5
    );
    mapRef.current = map;

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 19,
    }).addTo(map);

    L.control.scale({ imperial: false }).addTo(map);

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  /* =========================
     UPDATE MARKERS
     ========================= */
  useEffect(() => {
    if (!mapRef.current) return;

    markersRef.current.forEach((marker) => marker.remove());
    markersRef.current.clear();

    issues.forEach((issue) => {
      const { lat, lng } = issue.location || {};
      if (!lat || !lng) return;

      const isSelected = selectedIssue?.id === issue.id;

      const marker = L.marker([lat, lng], {
        icon: createCustomIcon(issue.riskScore, isSelected),
      }).addTo(mapRef.current!);
      
      marker.bindTooltip(
  `
  <div style="font-size:12px">
    <strong>${issue.title}</strong><br/>
    Status: ${issue.status.replace("_", " ")}<br/>
    ${
      issue.riskScore !== undefined
        ? `Risk: ${issue.riskScore}/100`
        : ""
    }
  </div>
  `,
  {
    direction: "top",
    offset: [0, -10],
    opacity: 0.95,
    sticky: true,
    className: "issue-tooltip",
  }
);

      marker.bindPopup(`
        <div style="min-width:200px">
          <h3 style="font-weight:600;margin-bottom:4px">${issue.title}</h3>
          <p style="font-size:12px;margin-bottom:6px">${issue.description}</p>
          <p style="font-size:12px"><strong>Status:</strong> ${issue.status.replace(
            "_",
            " "
          )}</p>
          ${
            issue.riskScore !== undefined
              ? `<p style="font-size:12px"><strong>Risk:</strong> ${issue.riskScore}/100</p>`
              : ""
          }
          <button
            style="margin-top:6px;font-size:12px;color:#2563eb"
            onclick="window.dispatchEvent(new CustomEvent('issue-select',{detail:'${issue.id}'}))"
          >
            View Full Report
          </button>
        </div>
      `);

      marker.on("click", () => {
        onIssueSelect?.(issue);
      });

      marker.on("mouseover", () => {
        marker.setZIndexOffset(1000);
      });

      marker.on("mouseout", () => {
        marker.setZIndexOffset(0);
      });

      markersRef.current.set(issue.id, marker);
    });

    const handleIssueSelect = (event: any) => {
      const issue = issues.find((i) => i.id === event.detail);
      if (issue) onIssueSelect?.(issue);
    };

    window.addEventListener("issue-select", handleIssueSelect as any);
    return () =>
      window.removeEventListener("issue-select", handleIssueSelect as any);
  }, [issues, selectedIssue, onIssueSelect]);

  /* =========================
     AUTO ZOOM
     ========================= */
  useEffect(() => {
    if (zoomToBounds && issues.length > 0) {
      const t = setTimeout(fitMapToMarkers, 100);
      return () => clearTimeout(t);
    }
  }, [issues, zoomToBounds, fitMapToMarkers]);

  /* =========================
     FLY TO SELECTED ISSUE
     ========================= */
  useEffect(() => {
    if (!mapRef.current || !selectedIssue) return;

    const { lat, lng } = selectedIssue.location || {};
    if (!lat || !lng) return;

    mapRef.current.flyTo([lat, lng], mapRef.current.getZoom(), {
  animate: true,
});

    markersRef.current.forEach((marker, id) => {
      const issue = issues.find((i) => i.id === id);
      if (issue) {
        marker.setIcon(
          createCustomIcon(issue.riskScore, id === selectedIssue.id)
        );
      }
    });

    const selectedMarker = markersRef.current.get(selectedIssue.id);
    if (selectedMarker) {
      setTimeout(() => selectedMarker.openPopup(), 400);
    }
  }, [selectedIssue, issues]);

  /* =========================
     RESIZE HANDLING
     ========================= */
  useEffect(() => {
    const handleResize = () => {
      setTimeout(() => mapRef.current?.invalidateSize(), 100);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <div className="relative" style={{ height: typeof height === "number" ? `${height}px` : height }}>
      <div
        ref={mapContainerRef}
        style={{ height: "100%" }}
        className={`rounded-lg shadow-md bg-gray-100 ${className}`}
      />
      
      {/* Legend Component */}
      {showLegend && (
        <div className="absolute bottom-4 left-4 bg-white p-3 rounded-lg shadow-md z-[1000] max-w-xs">
          <div className="flex justify-between items-center mb-2">
            <h3 className="text-sm font-semibold">Risk Legend</h3>
            <button 
              onClick={() => setShowLegend(false)}
              className="text-xs text-gray-500 hover:text-gray-700"
            >
              ×
            </button>
          </div>
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 rounded-full bg-red-500 border border-white"></div>
              <span className="text-xs">High Risk (70+)</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 rounded-full bg-yellow-500 border border-white"></div>
              <span className="text-xs">Medium Risk (40-69)</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 rounded-full bg-green-500 border border-white"></div>
              <span className="text-xs">Low Risk (0-39)</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 rounded-full bg-gray-400 border border-white"></div>
              <span className="text-xs">No Risk Data</span>
            </div>
          </div>
          <div className="mt-2 text-xs text-gray-500">
            Click markers for details
          </div>
        </div>
      )}
      
      {/* Toggle Legend Button */}
      {!showLegend && (
        <button
          onClick={() => setShowLegend(true)}
          className="absolute bottom-4 left-4 bg-white p-2 rounded-lg shadow-md z-[1000] text-xs font-medium hover:bg-gray-50"
        >
          Show Legend
        </button>
      )}
    </div>
  );
}

/* =========================
   MARKER STYLES
   ========================= */
const styles = `
.custom-marker {
  transition: all 0.3s ease;
  position: relative;
}
.custom-marker:hover {
  transform: scale(1.1);
  z-index: 1000;
}
.selected-marker {
  z-index: 1001;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.5);
}
.pulse {
  animation: pulse 2s infinite;
}
@keyframes pulse {
  0% { 
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.7);
  }
  50% { 
    transform: scale(1.05);
    box-shadow: 0 0 0 6px rgba(59, 130, 246, 0);
  }
  100% { 
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(59, 130, 246, 0);
  }
}
.leaflet-popup-content-wrapper {
  border-radius: 8px;
}
.issue-tooltip {
  border-radius: 6px;
  font-weight: 500;
}
`;

if (typeof document !== "undefined") {
  const styleSheet = document.createElement("style");
  styleSheet.innerText = styles;
  document.head.appendChild(styleSheet);
}